<script setup>
import { useCssModule } from 'vue';
import { isMetric } from '@@/utils/CommonUtils';
import { useUserStore } from '@@/stores/User';

const props = defineProps({
  canShowAmountCumulative: {
    type: Boolean,
    default: true,
  },
  isSkeleton: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    required: true,
  },
  showLeftBorder: {
    type: Boolean,
    default: true,
  },
  amountCumulative: {
    type: [Number, String],
    required: true,
  },
  amountCumulativeTextStyle: {
    type: Object,
    default() {
      return {};
    },
  },
});

const $style = useCssModule();
const userStore = useUserStore();

const units = computed(() => userStore.preferences.units);
const labelClass = computed(() => {
  return [
    'tw-text-center',
    'tw-text-xs lg:tw-text-sm ',
    'tw-leading-none lg:tw-leading-5',
    props.isSkeleton ? 'tw-font-medium text-skeleton-color' : 'tw-text-dark-color',
  ];
});

const amountCumulativeClass = computed(() => {
  return [
    'tw-relative',
    'tw-h-4 lg:tw-h-7 tw-mt-0.5 lg:tw-mt-1.5',
    'tw-border-r-2 border-color',
    props.showLeftBorder ? 'tw-border-l-2' : '',
    $style.subHeaderClass,
  ];
});

const amountCumulativeTextClass = computed(() => {
  return [
    'tw-absolute tw-inset-0',
    'tw-block tw-m-auto',
    props.isSkeleton ? 'tw-w-8 lg:tw-w-12 tw-border-x-4 border-color-card-background' : 'tw-w-16 lg:tw-w-20',
    'tw-text-center',
    'tw-text-sm lg:tw-text-xl tw-font-medium tw-whitespace-nowrap',
    props.isSkeleton ? 'skeleton-copy' : null,
  ];
});
</script>

<template>
  <div>
    <h2 :class="labelClass">
      {{ label }}
    </h2>
    <h3
      :class="amountCumulativeClass"
    >
      <span
        v-if="isSkeleton"
        :class="amountCumulativeTextClass"
      >
        {{ amountCumulative }}
      </span>
      <span
        v-else-if="canShowAmountCumulative"
        :class="amountCumulativeTextClass"
        :style="amountCumulativeTextStyle"
      >
        {{ amountCumulative }}<small
          v-if="isMetric(units)"
          class="tw-text-xs"
        >cm</small>
      </span>
      <font-awesome-icon
        v-else
        :class="[amountCumulativeTextClass, 'all-access-color']"
        :icon="['fas', 'lock']"
      />
    </h3>
  </div>
</template>

<style module>
/*
 * Display gray, horizontal line in the middle of the subheader, with a transparent section in the
 * middle where the amount cumulative number is displayed.
 * @see
 * - https://stackoverflow.com/questions/40697231/horizontal-line-in-the-middle-of-divs
 * - https://michaelharley.net/posts/2021/01/12/how-to-create-a-border-top-linear-gradient/
 */
.subHeaderClass::before {
  border-image: linear-gradient(
    to right,
    var(--border-color) 30%,
    var(--card-background) 50%,
    var(--border-color) 70%
  ) 1 0 0 0;
  border-top: 0.125rem solid;
  content: '';
  left: 0;
  position: absolute;
  top: calc(50% - 0.0625rem);
  width: 100%;
}
</style>
